import React, { useState, useEffect } from "react";
import { Box, Container, Typography, Avatar } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styled from "@emotion/styled";
import { fetchOrganigrama } from "../utils/contentfulRequests";
import placeholderImg from "../assets/account.png";

// ==========================
// Datos (3 niveles) - MOCK / fallback
// ==========================
const MOCK_ORG_DATA = {
  nombre: "GERENTE",
  puesto: "Vacante",
  foto: null,
  children: [
    {
      nombre: "SANIDAD",
      puesto: "Coordinador de Proyecto",
      descripcion: "I.P. Abraham G. Betancourt Ruiz",
      foto: null,
      children: [
        { nombre: "Laboratorista", puesto: "Zona Norte", foto: null },
        { nombre: "Laboratorista", puesto: "Zona Centro", descripcion: "I.P. Salvador González V.", foto: null },
        { nombre: "Laboratorista", puesto: "Zona Sur", descripcion: "I.P. Rosa E. Montaño de D.", foto: null },
        { nombre: "Prof. de Campo Peces", puesto: "I.P. Azucena López Barraza", foto: null },
        { nombre: "Prof. de Campo Moluscos", puesto: "Ocean. Gloria Padilla Lardizábal", foto: null },
        { nombre: "Prof. de Campo Crustáceos Norte", puesto: "I.P. Pablo Parra López", foto: null },
        { nombre: "Prof. De Campo Crustáceos Zona Centro I", puesto: "L.B. Mario Virgen Ruiz", foto: null },
        { nombre: "Prof. de Campo Crustáceos Centro II", puesto: "Ocean. Ricardo R. Tapia F.", foto: null },
        { nombre: "Prof. de Campo Crustáceos Sur", puesto: "M.C. Jessica A. Jiménez R.", foto: null },
      ],
    },
    {
      nombre: "INOCUIDAD",
      puesto: "Coordinador en SPRC",
      descripcion: "L.B. Beatriz E. Carrillo Ruíz",
      foto: null,
      children: [
        { nombre: "Profesional de Proyecto", puesto: "L.B.A. Luis Alfonso Jiménez Bravo", foto: null },
        { nombre: "Profesional de Proyecto", puesto: "I.P. Pablo B. Orozco Lara", foto: null },
        { nombre: "Profesional de Proyecto", puesto: "I.A. Modesta Lomelí Apodaca", foto: null },
      ],
    },
    {
      nombre: "VIGILANCIA EPIDEMIOLÓGICA",
      puesto: "Coordinador de Proyecto",
      descripcion: "L.B. Mariell Corona Ornelas",
      foto: null,
      children: [],
    },
    {
      nombre: "COORDINADOR ADMINISTRATIVO",
      puesto: "L.C. Ernesto Alonso González Ruíz",
      foto: null,
      children: [
        { nombre: "Aux. Administrativo", puesto: "L.I.G.P. Andreina Alcázar Sánchez", foto: null },
        { nombre: "Aux. Administrativo", puesto: "L.A.E. Kenny G. Tapia Cervantes", foto: null },
        { nombre: "Aux. de Informática", puesto: "L.I. Mayra Chanes Casillas", foto: null },
      ],
    },
  ],
};

// Distancias (px) para conectores
const ROW_GAP = 24; // separación vertical entre barra superior y tarjeta (niveles horizontales)
// const COL_CONNECT = 12; // longitud del conector en columnas (auxiliares) - no usado por ahora

// Placeholder: usar la imagen dentro de src/assets/account.png (la guardaste allí)
const PLACEHOLDER_SVG = placeholderImg;

const getAvatarSrc = (name, foto) => {
  if (foto) return foto;
  // puedes personalizar para generar initials-based SVG por nombre si quieres
  return PLACEHOLDER_SVG;
};

// ==========================
// Estilos base
// ==========================
const PageWrap = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(6),
}));

const Tree = styled("div")(() => ({
  width: "100%",
  overflowX: "auto",
}));

const TreeUL = styled("ul")(({ theme }) => ({
  listStyle: "none",
  margin: 0,
  padding: 0,
  textAlign: "center",
  position: "relative",

  // Fila horizontal (jefes)
  "&.row": {
    position: "relative",
    marginTop: ROW_GAP,
  },

  // Tronco vertical desde el padre hacia la fila de jefes
  "&.row:not(.root)::before": {
    content: '""',
    position: "absolute",
    top: -ROW_GAP,
    left: "50%",
    transform: "translateX(-50%)",
    height: ROW_GAP,
    borderLeft: '2px solid ' + theme.palette.divider,
  },

  // Cada jefe como item de la fila
  "&.row > li": {
    position: "relative",
    display: "inline-block",
    verticalAlign: "top",
    padding: ROW_GAP + 'px 8px 0 8px',
  },

  // Barra horizontal superior entre jefes (conectores entre hermanos)
  "&.row > li::before, &.row > li::after": {
    content: '""',
    position: "absolute",
    top: 0,
    borderTop: '2px solid ' + theme.palette.divider,
    width: "50%",
    height: ROW_GAP,
  },
  "&.row > li::before": { right: "50%" },
  "&.row > li::after": { left: "50%" },

  // Extremos y único hijo (sin barra)
  "&.row > li:first-of-type::before": { borderTop: "none" },
  "&.row > li:last-child::after": { borderTop: "none" },
  "&.row > li:only-child::before, &.row > li:only-child::after": { borderTop: "none" },

  // Conector vertical desde la barra superior al top de la tarjeta del jefe (línea pequeña)
  "&.row > li > .node::after": {
    content: '""',
    position: "absolute",
    top: -89,
    left: "50%",
    transform: "translateX(-50%)",
    height: 89,
    borderLeft: '2px solid ' + theme.palette.divider,
  },

  // La fila raíz (solo contiene al gerente) no dibuja tronco hacia arriba ni conectores
  "&.root": {
    marginTop: 0,
  },
  "&.root > li": {
    paddingTop: 0,
  },
  "&.root > li::before, &.root > li::after": {
    borderTop: "none",
  },
  "&.root > li > .node::before": {
    display: "none",
  },

  // Grid inferior de auxiliares (4 columnas)
  "&.gridBelow": {
    position: "relative",
    marginTop: theme.spacing(2),
    display: "grid",
    gridTemplateColumns: "repeat(4, 260px)",
    gap: theme.spacing(1.5),
    justifyItems: "center",
    justifyContent: "center",
  },
  "&.gridBelow > li": {
    position: "relative",
    width: "100%",
  },
}));

const TreeLI = styled("li")({});

const NodeCard = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  textAlign: "center",
  gap: theme.spacing(3),
  minWidth: 230,
  maxWidth: 300,
  height: 100,
  padding: theme.spacing(1.25, 1.5),
  paddingTop: theme.spacing(10),
  borderRadius: 8,
  border: '1px solid ' + theme.palette.divider,
  background: theme.palette.primary.main,
  color: theme.palette.common.white,
  boxShadow: theme.shadows[1],
  margin: "65px auto 0",
  overflow: "visible",
  "&[data-active='true']": {
    background: theme.palette.secondary.main,
  },
  "&[data-level='aux']": {
    background: theme.palette.highlight.main,
    color: theme.palette.text.primary,
  },
  transition: 'transform 0.18s cubic-bezier(.4,1.6,.6,1)',
  willChange: 'transform',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.04)',
    boxShadow: theme.shadows[4],
    zIndex: 2,
  },
}));

const AvatarCard = styled(Avatar)(() => ({
  width: 120,
  height: 120,
  fontSize: 24,
  // Asegura que la imagen interna se centre y recorte correctamente
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    display: 'block',
  },
  transition: 'transform 0.18s cubic-bezier(.4,1.6,.6,1)',
  willChange: 'transform',
  '.node:hover &': {
    transform: 'translateY(-6px) scale(1.07)',
    boxShadow: '0 8px 24px 0 rgba(0,0,0,0.10)',
  },
}));

const AvatarWrap = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: -70,
  left: '50%',
  transform: 'translateX(-50%)',
  background: theme.palette.common.white,
  borderRadius: '50%',
  padding: 3,
  boxShadow: theme.shadows[2],
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1,
}));

// ==========================
// Página
// ==========================
export default function Organigrama() {
  const [openIndex, setOpenIndex] = useState(null);
  const [orgData, setOrgData] = useState(MOCK_ORG_DATA);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const remote = await fetchOrganigrama();
        if (mounted && remote) {
          setOrgData(remote);
        }
      } catch (e) {
        // fallthrough to mock
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleToggle = (idx, hasChildren) => {
    if (!hasChildren) return;
    setOpenIndex((prev) => (prev === idx ? null : idx));
  };

  const jefes = Array.isArray(orgData.children) ? orgData.children : [];

  if (loading) {
    return (
      <PageWrap maxWidth="lg">
        <Typography variant="h5">Cargando organigrama...</Typography>
      </PageWrap>
    );
  }

  return (
    <PageWrap maxWidth="lg">
      <Typography variant="h4" component="h1" fontWeight={700} gutterBottom sx={{ fontSize: 22 }}>
        Organigrama
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Gerente y jefes visibles. Al hacer click en un jefe se despliegan sus auxiliares; al abrir otro, se cierra el anterior.
      </Typography>

      <Tree>
        {/* Nivel 1: Gerente (fila raíz) */}
        <TreeUL className="row root">
          <TreeLI>
            <NodeCard className="node" data-level="root">
              <AvatarWrap>
                <AvatarCard src={getAvatarSrc(orgData.nombre, orgData.foto)} alt={orgData.nombre} />
              </AvatarWrap>
              <Box textAlign="center">
                <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.15 }}>{orgData.nombre}</Typography>
                {orgData.descripcion && (
                  <Typography sx={{ fontSize: 12, color: "common.white" }}>{orgData.descripcion}</Typography>
                )}
                {orgData.puesto && (
                  <Typography sx={{ fontSize: 12, color: "common.white", opacity: 0.9 }}>{orgData.puesto}</Typography>
                )}
              </Box>
            </NodeCard>

            {/* Nivel 2: Jefes (fila horizontal) */}
            <TreeUL className="row jefes">
                {jefes.map((jefe, idx) => {
                const hasChildren = Array.isArray(jefe.children) && jefe.children.length > 0;
                const isOpen = openIndex === idx;
                return (
                  <TreeLI key={idx}>
                    <NodeCard
                      className="node"
                      data-level="jefe"
                      data-active={isOpen ? "true" : undefined}
                      role={hasChildren ? "button" : undefined}
                      tabIndex={hasChildren ? 0 : undefined}
                      onKeyDown={(e) => {
                        if (!hasChildren) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleToggle(idx, hasChildren);
                        }
                      }}
                      onClick={() => handleToggle(idx, hasChildren)}
                      sx={{ cursor: hasChildren ? "pointer" : "default" }}
                      aria-expanded={isOpen}
                      aria-haspopup={hasChildren}
                    >
                      <AvatarWrap>
                        <AvatarCard src={getAvatarSrc(jefe.nombre, jefe.foto)} alt={jefe.nombre} />
                      </AvatarWrap>
                      <Box textAlign="center">
                        <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.15 }}>{jefe.nombre}</Typography>
                        {jefe.descripcion && (
                          <Typography sx={{ fontSize: 12, color: "common.white" }}>{jefe.descripcion}</Typography>
                        )}
                        {jefe.puesto && (
                          <Typography sx={{ fontSize: 11.5, color: "common.white", opacity: 0.95 }}>{jefe.puesto}</Typography>
                        )}
                      </Box>
                      {hasChildren && (
                        <ExpandMoreIcon
                          sx={{ ml: 1, fontSize: 18, color: "common.white", transition: "transform .2s", transform: isOpen ? "rotate(180deg)" : "none" }}
                        />
                      )}
                    </NodeCard>

                                      </TreeLI>
                );
              })}
            </TreeUL>
            {openIndex !== null && Array.isArray(jefes[openIndex]?.children) && jefes[openIndex].children.length > 0 && (
              <TreeUL className="gridBelow aux" aria-live="polite">
                {jefes[openIndex].children.map((aux, aidx) => (
                  <TreeLI key={aidx}>
                    <NodeCard className="node" data-level="aux">
                    <AvatarWrap>
                      <AvatarCard src={getAvatarSrc(aux.nombre, aux.foto)} alt={aux.nombre} />
                    </AvatarWrap>
                    <Box textAlign="center" sx={{ overflowWrap: "anywhere", wordBreak: "break-word", hyphens: "auto" }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.15 }}>{aux.nombre}</Typography>
                        {aux.descripcion && (
                          <Typography sx={{ fontSize: 11.5, color: "text.primary" }}>{aux.descripcion}</Typography>
                        )}
                        {aux.puesto && (
                          <Typography sx={{ fontSize: 11, color: "text.primary", opacity: 0.95 }}>{aux.puesto}</Typography>
                        )}
                      </Box>
                    </NodeCard>
                  </TreeLI>
                ))}
              </TreeUL>
            )}
          </TreeLI>
        </TreeUL>
      </Tree>
    </PageWrap>
  );
}
